// Attribution: https://github.com/mui/material-ui/tree/master/docs/data/material/getting-started/templates/dashboard
import React from 'react';
import { Link } from 'react-router';

// MSAL
import { useIsAuthenticated, useMsal, useAccount } from "@azure/msal-react";

// Material
import { styled } from '@mui/material/styles';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import MuiAppBar from '@mui/material/AppBar';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';

// Global Components
import { AlertCommon } from './AlertCommon';
import { MainMenuListItems, SecondaryMenuListItems } from './MenuListItems';
import { SignInButton } from './SignInButton';
import { SignOutButton } from './SignOutButton';

const drawerWidth = 240;
const mobileLogo = '/images/simplified/coc-logo-simplify.svg';
const desktopLogo = '/images/coc-logo.svg';

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        '& .MuiDrawer-paper': {
            position: 'relative',
            whiteSpace: 'nowrap',
            width: drawerWidth,
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            boxSizing: 'border-box',
            ...(!open && {
                overflowX: 'hidden',
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.leavingScreen,
                }),
                width: theme.spacing(7),
                [theme.breakpoints.up('sm')]: {
                    width: theme.spacing(9),
                },
            }),
        },
    }),
);

// const mdTheme = createTheme();

const Navigation = ({ children = null }) => {
    const { accounts } = useMsal();
    const account = useAccount(accounts[0] || {});
    const isAuthenticated = useIsAuthenticated();

    const [open, setOpen] = React.useState(false);

    const toggleDrawer = () => {
        setOpen(!open);
    };

    return (
        // <ThemeProvider theme={mdTheme}>
        <Box
            sx={{
                display: 'flex',
                position: 'relative',
                flexGrow: 1
            }}
        >
            <CssBaseline />
            <AppBar position="absolute" open={open} color="transparent">
                <Toolbar sx={{
                    maxHeight: 0,
                    paddingRight: '24px'
                }}>
                    {/*<IconButton
                        edge="start"
                        color="inherit"
                        aria-label="expand menu"
                        onClick={toggleDrawer}
                        sx={{
                            marginRight: '36px',
                            ...(open && { visibility: 'none' }),
                        }}
                    >
                        <Tooltip title="Expand Menu" placement="bottom" arrow>
                            <MenuIcon />
                        </Tooltip>
                    </IconButton>*/}
                    <Link to='/'>
                        <img
                            src={mobileLogo}
                            alt="Return to City Services Notifications homepage"
                            className="coc-logo coc-logo-mobile no-print"
                            loading="lazy"
                        />
                        <img
                            src={desktopLogo}
                            alt="Return to City Services Notifications homepage"
                            className="coc-logo coc-logo-desktop"
                            loading="lazy"
                        />
                    </Link>
                    {/* //show env only on non prod builds */}
                    {process.env.REACT_APP_ENV !== 'production' &&
                        <Badge
                            sx={{
                                marginTop: '-2rem',
                                marginLeft: '1.5rem'
                            }}
                            color="primary"
                            badgeContent={process.env.REACT_APP_ENV} />
                    }
                    <Typography
                        sx={{
                            flex: 1,
                            pl: '10px',
                            display: { xs: 'none', sm: 'block' }
                        }}
                        component="h1"
                        variant="h6"
                        color="inherit"
                        noWrap
                    >
                        {process.env.REACT_APP_NAME}
                    </Typography>
                    <Typography
                        sx={{
                            flex: 1,
                            pl: '10px',
                            display: { xs: 'block', sm: 'none' }
                        }}
                        component="h1"
                        variant="h6"
                        color="inherit"
                        noWrap
                    >
                        {process.env.REACT_APP_SHORT_NAME}
                    </Typography>
                    {isAuthenticated ?
                        <div style={{ paddingRight: "10px" }}>
                            <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
                                Welcome, {account?.idTokenClaims.given_name + ' ' + account?.idTokenClaims.family_name }
                            </Typography>
                        </div>
                        : null}
                    {isAuthenticated ? <SignOutButton /> : <SignInButton />}
                </Toolbar>
            </AppBar>

            {/*<Drawer variant="permanent" open={open}>
                <Toolbar sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: [1],
                }}>
                    <IconButton
                        onClick={toggleDrawer}
                        aria-label="collapse menu"
                        sx={{
                            ...(!open && { visibility: 'hidden' }),
                        }}
                    >
                        <Tooltip title="Collapse Menu" placement="bottom" arrow>
                            <ChevronLeftIcon />
                        </Tooltip>
                    </IconButton>
                </Toolbar>
                <Divider />
                <List component="nav"><MainMenuListItems /></List>
                <Divider />
                <List component="nav"><SecondaryMenuListItems /></List>
            </Drawer>*/}

            <Box
                component="main"
                sx={{
                    flex: 1,
                    overflow: 'auto',
                    marginTop: '4rem',
                    backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                            ? theme.palette.grey[100]
                            : theme.palette.grey[900]
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: 4,
                        mb: 4,
                        overflow: 'auto'
                    }}
                >
                    <AlertCommon id="default-alert" />
                    {children}
                </Container>
            </Box>
        </Box >
        // </ThemeProvider >
    );
}

export { Navigation };
